import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { LoginCredentials } from '../types/auth'

export default function LoginForm() {
  const { login, loading } = useAuth()
  const navigate = useNavigate()
  const [formData, setFormData] = useState<LoginCredentials>({
    email: '',
    password: ''
  })
  const [errors, setErrors] = useState<Partial<LoginCredentials>>({})
  const [submitError, setSubmitError] = useState<string>('')

  const validateForm = (): boolean => {
    const newErrors: Partial<LoginCredentials> = {}

    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email'
    }

    if (!formData.password) {
      newErrors.password = 'Password is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError('')

    if (!validateForm()) return

    try {
      await login(formData)
      // Redirect to dashboard after successful login
      navigate('/dashboard')
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Login failed')
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name as keyof LoginCredentials]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
  }

  return (
    <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray">
      <h3 className="text-xl font-bold text-white mb-6 text-center">Login</h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {submitError && (
          <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 text-red-300 text-sm">
            {submitError}
          </div>
        )}

        <div>
          <label htmlFor="sidebar-email" className="block text-sm font-medium text-gray-300 mb-2">
            Email
          </label>
          <input
            id="sidebar-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            className={`input-field ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder="Enter your email"
            value={formData.email}
            onChange={handleInputChange}
            disabled={loading}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-400">{errors.email}</p>
          )}
        </div>

        <div>
          <label htmlFor="sidebar-password" className="block text-sm font-medium text-gray-300 mb-2">
            Password
          </label>
          <input
            id="sidebar-password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            className={`input-field ${errors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder="Enter your password"
            value={formData.password}
            onChange={handleInputChange}
            disabled={loading}
          />
          {errors.password && (
            <p className="mt-1 text-sm text-red-400">{errors.password}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </button>

        <div className="text-center space-y-2">
          <Link 
            to="/register" 
            className="block text-firedup-orange hover:text-firedup-red font-medium transition-colors text-sm"
          >
            Sign Up
          </Link>
          <Link 
            to="/forgot-password" 
            className="block text-gray-400 hover:text-gray-300 transition-colors text-sm"
          >
            Forgot password?
          </Link>
        </div>
      </form>
    </div>
  )
}
